import 'regenerator-runtime/runtime';
import 'src/index.css';
import { initSentry } from 'src/integrations/sentry/sentry';
import { initDemoCompanyNameReplacer } from 'src/lib/demoCompanyNameReplacer';
import { StrictMode, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthContext, AuthProvider } from 'src/context/auth.context';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from 'src/api/queryClient';
import { MaterialUIControllerProvider } from 'src/md-context';
import { ToastProvider } from 'src/context/ToastContext';
import { ThemeProvider } from '@mui/material/styles';
import theme from 'src/assets/md-theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AnalyticsProvider } from 'src/context/analytics.context';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { routeTree } from './routes';
import { useIsModuleEnabled } from './hooks/organization-config/useOrganizationEnabledModules';
import { AppSkeleton } from './AppSkeleton';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { authzClient } from 'src/context/authz/AuthzClient';
import {
  AlertContainer,
  AlertTitle,
  AlertDescription,
} from 'src/components/ui/alert';

initSentry();
initDemoCompanyNameReplacer();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  context: {
    auth: undefined!,
    queryClient,
    isModuleEnabled: () => false,
    authzClient,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function RouteProviderWithContext() {
  const auth = useContext(AuthContext);
  const { isModuleEnabled, isLoading: isModulesLoading } = useIsModuleEnabled();

  if (auth.isFetchingAuthInitialStatus || isModulesLoading) {
    return <AppSkeleton />;
  }

  return (
    <>
      {auth.errorLoadingUser && (
        <div className="fixed top-0 left-0 right-0 z-50 p-3">
          <AlertContainer variant="destructive" className="shadow-lg">
            <WarningCircleIcon />
            <AlertTitle>Something Went Wrong</AlertTitle>
            <AlertDescription>
              {`We're having trouble connecting. Please try again or contact support.`}
            </AlertDescription>
            <div className="flex gap-2 mt-3 col-start-2">
              <Button
                size="sm"
                onClick={() => auth.refetchUser()}
                disabled={auth.isRefetchingUser}
              >
                {auth.isRefetchingUser ? 'Retrying...' : 'Try Again'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.location.assign('/login')}
              >
                Back to Login
              </Button>
            </div>
          </AlertContainer>
        </div>
      )}
      <RouterProvider
        router={router}
        context={{ auth, queryClient, isModuleEnabled }}
      />
    </>
  );
}

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <QueryClientProvider client={queryClient}>
            <MaterialUIControllerProvider>
              <ToastProvider>
                <AuthProvider>
                  <AnalyticsProvider>
                    <RouteProviderWithContext />
                  </AnalyticsProvider>
                </AuthProvider>
              </ToastProvider>
            </MaterialUIControllerProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}
